import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { questionBankService } from '../services/questionBankService';
import { QuestionBank, BankQuestion } from '../types';
import {
  ArrowLeftIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  CopyIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ClipboardIcon,
  FileTextIcon,
} from '../components/Icons';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../context/ToastContext';

const QuestionBankDetailPage: React.FC = () => {
  const { bankId } = useParams<{ bankId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [bank, setBank] = useState<QuestionBank | null>(null);
  const [questions, setQuestions] = useState<BankQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; questionId: number | null }>({ show: false, questionId: null });

  useEffect(() => {
    if (bankId) {
      fetchData();
    }
  }, [bankId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bankData, questionsData] = await Promise.all([
        questionBankService.getBank(Number(bankId)),
        questionBankService.getBankQuestions(Number(bankId)),
      ]);
      setBank(bankData);
      setQuestions(questionsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      showToast('加载失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (questionId: number) => {
    try {
      // Get the question to copy
      const source = questions.find(q => q.id === questionId);
      if (!source) return;
      await questionBankService.createBankQuestion(Number(bankId), {
        title: source.title + '（复制）',
        type: source.type,
        imageUrl: source.imageUrl,
        required: source.required,
        orderNum: questions.length,
        questionNumber: source.questionNumber,
        settings: source.settings,
        options: source.options.map(opt => ({
          content: opt.content,
          imageUrl: opt.imageUrl,
          score: opt.score,
          orderNum: opt.orderNum,
          isOtherOption: opt.isOtherOption,
        })),
      });
      showToast('题目已复制', 'success');
      fetchData();
    } catch (error: any) {
      showToast(error.response?.data?.message || '复制失败', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.questionId) return;
    try {
      await questionBankService.deleteBankQuestion(Number(bankId), deleteConfirm.questionId);
      showToast('题目已删除', 'success');
      setDeleteConfirm({ show: false, questionId: null });
      fetchData();
    } catch (error: any) {
      showToast(error.response?.data?.message || '删除失败', 'error');
    }
  };

  const getQuestionTypeIcon = (type: string): string => {
    const icons: Record<string, string> = {
      SINGLE_CHOICE: '◉',
      MULTIPLE_CHOICE: '☑',
      TEXT: '⌨',
      SLIDER: '─',
      MATRIX: '▦',
      DESCRIPTION: '📄',
      FILL_IN_BLANK: '___',
      DYNAMIC_FILL_IN_BLANK: '＋___',
    };
    return icons[type] || '?';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-on-surface-variant font-body">加载中...</div>
      </div>
    );
  }

  if (!bank) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-on-surface-variant font-body">题库不存在</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/question-banks')}
          className="flex items-center gap-1 text-sm text-on-surface-variant hover:text-on-surface mb-4"
        >
          <ArrowLeftIcon size={16} />
          返回题库列表
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClipboardIcon className="text-primary-600" size={24} />
            <div>
              <h1 className="text-2xl font-bold font-headline text-on-surface">{bank.name}</h1>
              {bank.description && (
                <p className="text-sm text-on-surface-variant mt-1 font-body">{bank.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-on-surface-variant font-label">{bank.questionCount} 道题目</span>
          </div>
        </div>
      </div>

      {/* Question List */}
      {questions.length === 0 ? (
        <div className="text-center py-12 bg-surface-container-lowest rounded-3xl shadow-soft">
          <FileTextIcon className="mx-auto mb-4 text-on-surface-variant" size={48} />
          <h3 className="text-lg font-medium font-headline text-on-surface mb-2">暂无题目</h3>
          <p className="text-on-surface-variant mb-4 font-body">开始向题库中添加题目</p>
          <button
            onClick={() => navigate(`/question-banks/${bankId}/questions/new/edit`)}
            className="inline-flex items-center gap-2 px-4 py-2 gradient-primary text-on-primary shadow-teal rounded-xl hover:opacity-90"
          >
            <PlusIcon size={20} />
            添加题目
          </button>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold font-headline text-on-surface">题目列表</h2>
            <button
              onClick={() => navigate(`/question-banks/${bankId}/questions/new/edit`)}
              className="flex items-center gap-2 px-4 py-2 gradient-primary text-on-primary shadow-teal rounded-xl hover:opacity-90"
            >
              <PlusIcon size={20} />
              添加题目
            </button>
          </div>

          <div className="space-y-4">
            {questions.map((question, index) => (
              <div
                key={question.id}
                className="bg-surface-container-lowest p-6 rounded-2xl shadow-soft hover:shadow-soft-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-medium text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-medium">{getQuestionTypeIcon(question.type)}</span>
                        {question.type === 'DESCRIPTION' ? (
                          <h3 className="text-lg font-medium text-on-surface-variant italic">说明文本</h3>
                        ) : (
                          <>
                            <h3 className="text-lg font-medium text-on-surface">{question.title}</h3>
                            {question.required && (
                              <span className="text-danger-600 text-sm font-label">*必填</span>
                            )}
                          </>
                        )}
                      </div>
                      <span className="px-2 py-1 bg-surface-container-high text-on-surface-variant rounded text-xs font-label">
                        {question.typeDisplayName}
                      </span>
                    </div>

                    {question.imageUrl && (
                      <div className="mb-3">
                        <img
                          src={question.imageUrl}
                          alt="Question"
                          className="max-w-xs rounded-xl shadow-soft"
                        />
                      </div>
                    )}

                    {(question.type === 'SINGLE_CHOICE' || question.type === 'MULTIPLE_CHOICE') && (
                      <div className="mt-3 space-y-2">
                        {question.options.map((option, optIndex) => (
                          <div key={option.id} className="flex items-center gap-3 text-sm text-on-surface font-body">
                            <span className="text-on-surface-variant">{String.fromCharCode(65 + optIndex)}.</span>
                            <span>{option.content}</span>
                            {option.score !== null && option.score !== undefined && (
                              <span className="text-xs text-on-surface-variant font-label">({option.score}分)</span>
                            )}
                            {option.isOtherOption && (
                              <span className="text-xs text-primary-600 font-label">(其他选项)</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {question.type === 'DESCRIPTION' && question.settings && (
                      <div className="mt-2 text-sm text-on-surface-variant bg-blue-50 rounded-xl p-3 line-clamp-3 font-body" dangerouslySetInnerHTML={{ __html: question.settings }} />
                    )}

                    {question.type === 'MATRIX' && question.settings && (() => {
                      try {
                        const config = JSON.parse(question.settings);
                        const rows: string[] = config.rows || [];
                        const columns: string[] = config.columns || [];
                        const scores: Record<string, Record<string, number>> | undefined = config.scores;
                        const hasScores = scores && Object.keys(scores).length > 0;
                        return (
                          <div className="mt-3 overflow-x-auto">
                            <table className="border-collapse text-sm">
                              <thead>
                                <tr>
                                  <th className="p-2 border border-gray-200 bg-surface-container-low min-w-[80px]"></th>
                                  {columns.map((col: string) => (
                                    <th key={col} className="p-2 border border-gray-200 bg-surface-container-low text-center text-on-surface-variant font-medium font-label min-w-[60px]">
                                      {col}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map((row: string) => (
                                  <tr key={row}>
                                    <td className="p-2 border border-gray-200 text-on-surface font-medium font-body">{row}</td>
                                    {columns.map((col: string) => (
                                      <td key={col} className="p-2 border border-gray-200 text-center">
                                        <span className="text-on-surface-variant">○</span>
                                        {hasScores && scores[row]?.[col] !== undefined && (
                                          <span className="block text-xs text-on-surface-variant mt-0.5 font-label">{scores[row][col]}分</span>
                                        )}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      } catch {
                        return null;
                      }
                    })()}

                    <div className="flex items-center gap-3 mt-4">
                      <button
                        onClick={() => navigate(`/question-banks/${bankId}/questions/${question.id}/edit`)}
                        className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                      >
                        <EditIcon size={16} />
                        编辑
                      </button>
                      <button
                        onClick={() => handleCopy(question.id)}
                        className="flex items-center gap-1 text-sm text-on-surface-variant hover:text-on-surface"
                      >
                        <CopyIcon size={16} />
                        复制
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ show: true, questionId: question.id })}
                        className="flex items-center gap-1 text-sm text-danger-600 hover:text-danger-700"
                      >
                        <TrashIcon size={16} />
                        删除
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={deleteConfirm.show}
        title="删除题目"
        message="确定要删除此题目吗？"
        confirmText="删除"
        cancelText="取消"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ show: false, questionId: null })}
        type="danger"
      />
    </div>
  );
};

export default QuestionBankDetailPage;
